"use client";
import React, { useCallback, useMemo, useState } from "react";
import styles from "./BlogsHomeNew.module.scss";
import clsx from "clsx";
import LeftArrowNoTail from "@/SvgIcons/LeftArrowNoTail";
import RightArrowNoTail from "@/SvgIcons/RightArrowNoTail";
import { stripHtmlTags } from "@/api/strapiSingleAttribute";
import Calender from "@/SvgIcons/Calender";
import CommonButton from "@/components/common/Analytics/CustomButton";
import { addStaticSubdomain } from "@/revamp/molecules/ProgramCard/utils";
import UnivoImage from "@/components/common/UnivoImage";
import UnivoCarousel, {
  useUnivoCarousel,
} from "@/components/common/UnivoCarousel";
import type { UnivoCarouselSettings } from "@/components/common/UnivoCarousel/types";

const Blogs = ({ BlogsSection, data }) => {
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const { ref: sliderRef, next: handleNext, prev: handlePrev } =
    useUnivoCarousel();
  const CTA = BlogsSection?.CTA || {};

  // Avoid remark/micromark (~30–40 KiB) — title is only used for GA section_name.
  const heading = useMemo(
    () => stripHtmlTags(BlogsSection?.Title?.text || ""),
    [BlogsSection?.Title?.text]
  );

  const handleSlideChange = useCallback(
    (swiper: { isBeginning: boolean; isEnd: boolean }) => {
      setIsAtStart(swiper.isBeginning);
      setIsAtEnd(swiper.isEnd);
    },
    []
  );

  const carouselSettings = useMemo<UnivoCarouselSettings>(
    () => ({
      pagination: {
        status: false,
        className: `${styles.bulletsContainer} revamp_carousel_pagination`,
      },
      onSlideChange: handleSlideChange,
    }),
    [handleSlideChange]
  );

  return (
    <div className={clsx(styles.blogsOnlineContainer, "lining-nums")}>
      <h2 className={styles.blogsOnlineTitle} id="latest-blog-heading">Knowledge Hub: Amity Online</h2>
      <div className={clsx(styles.blogCaruContainer, "relative")}>
        <UnivoCarousel
          ref={sliderRef}
          preset="blogs"
          settings={carouselSettings}
          className={`${styles.blogsCarouselWrapper} relative`}
          slidesContainerClassName={styles.slidesContainer}
          slideWrapperClassName={styles.slideWrapper}
          isSmoothBehaviour={true}
          id="blog-card"
        >
          {data.map((item, index) => {
            const datePosted = new Date(item?.attributes?.updatedAt);
            /* timeZone pinned so server and client render the same date (no hydration mismatch) */
            const formattedDate = datePosted.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
              timeZone: "UTC",
            });
            return (
              <a href={item?.attributes?.url_key || "#"} key={index} className={styles.blogsOnlineItem}>
                <UnivoImage
                  src={addStaticSubdomain(item?.attributes?.BannerImage?.data?.attributes?.url)}
                  alt={
                    item?.attributes?.BannerImage?.data?.attributes
                      ?.alternativeText || "amity online"
                  }
                  height={200}
                  width={302}
                  sizes="(max-width: 768px) 90vw, 302px"
                  className={styles.cardImg}
                  loading="lazy"
                  fetchPriority="low"
                />
                <div className={styles.content}>
                  <div className={styles.date}>
                    <Calender />
                    {formattedDate || ""}
                  </div>
                  <h3 className={styles.heading}>
                    {item?.attributes?.Heading || ""}
                  </h3>
                </div>
              </a>
            );
          })}
        </UnivoCarousel>
        <button
          className={clsx(styles.prevArrow)}
          onClick={handlePrev}
          disabled={isAtStart}
          aria-label="Previous"
        >
          <LeftArrowNoTail />
        </button>
        <button
          className={clsx(styles.nextArrow)}
          onClick={handleNext}
          aria-label="Next"
          disabled={isAtEnd}
        >
          <RightArrowNoTail />
        </button>
        <div className={styles.ctaContainer}>
          <CommonButton
            GADataNew={{
              event: "view_all_cta_click",
              cta_text: CTA.text,
              section_name: heading,
              isPageType: true,
            }}
            isEventOnly
            href={CTA?.url}
            CTATarget={CTA?.target?.[0]}
            type={CTA?.url === "#" ? "button" : "link"}
            className={styles.ctaBtn}
            bg="o"
            color="b"
            id="blog-view-all"
          >
            view more
          </CommonButton>
        </div>
      </div>
    </div>
  );
};

export default Blogs;
